import React, { useState, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { Search, X, Check, ChevronDown } from 'lucide-react';
import { FilterPopover, FilterPanel, FilterSection, FilterOptionList } from './FilterPanel';

/**
 * Date range picker for the filter right panel
 */
const FilterDateRange = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    startLabel: startLabelProp,
    endLabel: endLabelProp,
    startError = '',
    endError = '',
}) => {
    const { t } = useTranslation();
    const today = new Date().toISOString().split('T')[0];
    const startLabel = startLabelProp || t('paymentpage.start_date', 'Start Date');
    const endLabel = endLabelProp || t('paymentpage.end_date', 'End Date');

    return (
        <div className="space-y-3" data-testid="payment-filter-date-range-container">
            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1" data-testid="payment-filter-start-date-label">{startLabel}</label>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    max={endDate || today}
                    className={`w-full px-3 py-2.5 text-sm bg-gray-50 border rounded-lg focus:outline-none focus:ring-1 transition-all ${
                        startError
                            ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/30'
                            : 'border-gray-200 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/30'
                    }`}
                    data-testid="payment-filter-start-date-input"
                />
                {startError && <p className="text-xs text-red-500 mt-1" data-testid="payment-filter-start-date-error">{startError}</p>}
            </div>
            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1" data-testid="payment-filter-end-date-label">{endLabel}</label>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    min={startDate || undefined}
                    max={today}
                    className={`w-full px-3 py-2.5 text-sm bg-gray-50 border rounded-lg focus:outline-none focus:ring-1 transition-all ${
                        endError
                            ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/30'
                            : 'border-gray-200 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/30'
                    }`}
                    data-testid="payment-filter-end-date-input"
                />
                {endError && <p className="text-xs text-red-500 mt-1" data-testid="payment-filter-end-date-error">{endError}</p>}
            </div>
        </div>
    );
};

/**
 * Searchable patient list for the filter right panel.
 *
 * Modes:
 *  - Single-select (default, `multiple=false`): `value` is a patientId string;
 *    `onChange(id)`. The "All" row clears to ''.
 *  - Multi-select (`multiple=true`): `value` is an array of patientIds;
 *    `onChange(newArray)`. The "All" row clears to [].
 */
const FilterPatientList = ({
    patients = [],
    value,
    onChange,
    searchPlaceholder,
    noResultsText,
    allLabel,
    isLoading = false,
    multiple = false,
}) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const filtered = patients.filter(
        (p) =>
            p.name?.toLowerCase().includes(search.toLowerCase()) ||
            p.id?.toLowerCase().includes(search.toLowerCase())
    );
    const selectedArray = multiple ? (Array.isArray(value) ? value : []) : null;
    const isAllSelected = multiple ? selectedArray.length === 0 : !value;

    const handleAllClick = () => {
        if (multiple) onChange([]);
        else onChange('');
    };

    return (
        <div data-testid="payment-filter-patient-list-container">
            {/* All Patients option */}
            <button
                type="button"
                onClick={handleAllClick}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 cursor-pointer mb-2 ${
                    isAllSelected
                        ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="payment-filter-all-patients-button"
            >
                <div
                    className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                        isAllSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]' : 'border-gray-300'
                    }`}
                >
                    {isAllSelected && <Check className="w-3 h-3 text-white" data-testid="payment-filter-all-patients-check-icon" />}
                </div>
                <span className="truncate text-left" data-testid="payment-filter-all-patients-text">{allLabel || t("common.all")}</span>
            </button>

            {/* Search */}
            <div className="relative mb-2" data-testid="payment-filter-patient-search-container">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-testid="payment-filter-patient-search-icon" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value.replace(/[<>]/g, '').substring(0, 50))}
                    placeholder={searchPlaceholder || t("common.search")}
                    className="w-full pl-8 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                    data-testid="payment-filter-patient-search-input"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
                        data-testid="payment-filter-patient-search-clear-button"
                    >
                        <X className="w-3.5 h-3.5 text-gray-400" data-testid="payment-filter-patient-search-clear-icon" />
                    </button>
                )}
            </div>

            {/* Patient list */}
            <div className="space-y-0.5 max-h-[200px] overflow-y-auto custom-scrollbar" data-testid="payment-filter-patient-list">
                {isLoading ? (
                    <div className="flex justify-center py-6" data-testid="payment-filter-patient-loading">
                        <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-400" data-testid="payment-filter-patient-no-results">{noResultsText || t("common.no_results")}</div>
                ) : (
                    filtered.map((patient) => {
                        const isSelected = multiple
                            ? selectedArray.includes(patient.id)
                            : value === patient.id;
                        const handleClick = () => {
                            if (multiple) {
                                const next = isSelected
                                    ? selectedArray.filter((id) => id !== patient.id)
                                    : [...selectedArray, patient.id];
                                onChange(next);
                            } else {
                                onChange(isSelected ? '' : patient.id);
                            }
                        };
                        return (
                            <button
                                key={patient.id}
                                type="button"
                                onClick={handleClick}
                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 cursor-pointer ${
                                    isSelected
                                        ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                                        : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                data-testid={`payment-filter-patient-option-${patient.id}`}
                            >
                                <div
                                    className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                                        isSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]' : 'border-gray-300'
                                    }`}
                                >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className="truncate text-left">{patient.name}</span>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

/**
 * Payment filter panel component using the standard FilterPanel layout.
 * Two-panel design matching FilterPanel.jsx pattern.
 */
const PaymentFilterPanel = ({
    tempFilters,
    onTempFiltersChange,
    dateErrors,
    onDateErrorsChange,
    onApply,
    onClear,
    patientOptions = [],
    userRole,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const today = new Date().toISOString().split('T')[0];

    const paymentModeOptions = [
        { value: 'cash', label: t('paymentpage.payment_mode_cash') },
        { value: 'card', label: t('paymentpage.payment_mode_card', 'Card / Terminal') },
        { value: 'online', label: t('paymentpage.payment_mode_online', 'Online') },
        // Legacy values kept so historical records remain filterable.
        { value: 'Netbanking', label: t('paymentpage.payment_mode_netbanking') },
    ];

    const paymentStatusOptions = [
        { value: 'paid', label: t('paymentpage.payment_status_paid') },
        { value: 'notpaid', label: t('paymentpage.payment_status_notpaid') },
    ];

    const validateDates = (startDate, endDate) => {
        const errors = { startDate: '', endDate: '' };
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            errors.startDate = t('paymentpage.start_date_after_end');
        }
        if (endDate && new Date(endDate) > new Date(today)) {
            errors.endDate = t('paymentpage.end_date_future');
        }
        return errors;
    };

    const handleStartDateChange = (value) => {
        onTempFiltersChange({ ...tempFilters, startDate: value });
        onDateErrorsChange(validateDates(value, tempFilters.endDate));
    };

    const handleEndDateChange = (value) => {
        onTempFiltersChange({ ...tempFilters, endDate: value });
        onDateErrorsChange(validateDates(tempFilters.startDate, value));
    };

    return (
        <FilterPopover>
            <FilterPanel
                onApply={onApply}
                onClear={onClear}
                applyLabel={t('paymentpage.apply')}
                clearLabel={t('paymentpage.clear')}
            >
                <FilterSection
                    title={t('paymentpage.by_date')}
                    isActive={!!(tempFilters.startDate || tempFilters.endDate)}
                >
                    <FilterDateRange
                        startDate={tempFilters.startDate}
                        endDate={tempFilters.endDate}
                        onStartDateChange={handleStartDateChange}
                        onEndDateChange={handleEndDateChange}
                        startLabel={t('paymentpage.start_date', 'Start Date')}
                        endLabel={t('paymentpage.end_date', 'End Date')}
                        startError={dateErrors.startDate}
                        endError={dateErrors.endDate}
                    />
                </FilterSection>

                <FilterSection
                    title={t('paymentpage.by_mode')}
                    isActive={Array.isArray(tempFilters.mode) ? tempFilters.mode.length > 0 : !!tempFilters.mode}
                >
                    <FilterOptionList
                        multiple
                        options={paymentModeOptions}
                        value={Array.isArray(tempFilters.mode) ? tempFilters.mode : (tempFilters.mode ? [tempFilters.mode] : [])}
                        onChange={(val) => onTempFiltersChange({ ...tempFilters, mode: val })}
                    />
                </FilterSection>

                <FilterSection
                    title={t('paymentpage.by_status')}
                    isActive={Array.isArray(tempFilters.status) ? tempFilters.status.length > 0 : !!tempFilters.status}
                >
                    <FilterOptionList
                        multiple
                        options={paymentStatusOptions}
                        value={Array.isArray(tempFilters.status) ? tempFilters.status : (tempFilters.status ? [tempFilters.status] : [])}
                        onChange={(val) => onTempFiltersChange({ ...tempFilters, status: val })}
                    />
                </FilterSection>

                {userRole === 'doctor' && (
                    <FilterSection
                        title={t('paymentpage.by_patient')}
                        isActive={Array.isArray(tempFilters.patientId) ? tempFilters.patientId.length > 0 : !!tempFilters.patientId}
                    >
                        <FilterPatientList
                            multiple
                            patients={patientOptions}
                            value={Array.isArray(tempFilters.patientId) ? tempFilters.patientId : (tempFilters.patientId ? [tempFilters.patientId] : [])}
                            onChange={(val) => onTempFiltersChange({ ...tempFilters, patientId: val })}
                            searchPlaceholder={t('paymentpage.search_patient')}
                            noResultsText={t('paymentpage.no_patients_found')}
                            allLabel={t('paymentpage.all_patients')}
                            isLoading={isLoading}
                        />
                    </FilterSection>
                )}
            </FilterPanel>
        </FilterPopover>
    );
};

export default PaymentFilterPanel;
